import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Contact() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5qdi12hs78ip537070g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
    
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-medium text-stone-800 mb-6">{t('contact_hero_title')}</h1>
          <p className="text-lg text-stone-600 leading-relaxed mb-4">
            {t('contact_hero_desc')}
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 inline-block">
            <p className="text-amber-800 font-medium text-sm">
              <i className="ri-alert-line mr-2"></i>
              {t('contact_emergency_note')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {/* Email Contact */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-amber-600"></i>
                </div>
                <div className="flex-1">
                  <div className="text-sm uppercase tracking-wide text-stone-500 mb-1">{t('contact_methods_email_label')}</div>
                  <a 
                    href="mailto:contact@valhallaearth.com" 
                    className="text-xl font-semibold text-stone-800 hover:text-amber-600 transition-colors duration-200"
                  >
                    {t('contact_email')}
                  </a>
                  <p className="text-stone-600 text-sm mt-2">
                    {t('contact_methods_email_desc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Telegram Contact */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-telegram-line text-xl text-amber-600"></i>
                </div>
                <div className="flex-1">
                  <div className="text-sm uppercase tracking-wide text-stone-500 mb-1">{t('contact_methods_telegram_label')}</div>
                  <a 
                    href="https://t.me/ValhallaEarth" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xl font-semibold text-stone-800 hover:text-amber-600 transition-colors duration-200"
                  >
                    {t('contact_telegram')}
                  </a>
                  <p className="text-stone-600 text-sm mt-2">
                    {t('contact_methods_telegram_desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-medium text-stone-800 mb-4">{t('contact_form_title')}</h2>
            <p className="text-stone-600 leading-relaxed">
              {t('contact_form_desc')}
            </p>
          </div>

          <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                  {t('contact_form_name')} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full h-12 px-4 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                  {t('contact_form_email')} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full h-12 px-4 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-stone-700 mb-2">
                {t('contact_form_subject')}
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full h-12 px-4 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
              >
                <option value="">{t('contact_form_subject_select')}</option>
                <option value="veteran-support">{t('contact_form_subject_veteran')}</option>
                <option value="family-support">{t('contact_form_subject_family')}</option>
                <option value="general-inquiry">{t('contact_form_subject_general')}</option>
                <option value="volunteer">{t('contact_form_subject_volunteer')}</option>
                <option value="other">{t('contact_form_subject_other')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                {t('contact_form_message')} *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                maxLength={500}
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200 resize-none"
                placeholder={t('contact_form_message_placeholder')}
              />
              <div className="text-right text-xs text-stone-500 mt-1">
                {formData.message.length}/500 {t('contact_form_characters')}
              </div>
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="bg-stone-800 text-white px-8 py-4 rounded-full font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 whitespace-nowrap"
              >
                {isSubmitting ? t('contact_form_sending') : t('contact_form_submit')}
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <i className="ri-check-circle-line text-green-600 text-xl mb-2"></i>
                <p className="text-green-800 font-medium">{t('contact_success_title')}</p>
                <p className="text-green-700 text-sm">{t('contact_success_desc')}</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <i className="ri-error-warning-line text-red-600 text-xl mb-2"></i>
                <p className="text-red-800 font-medium">{t('contact_error_title')}</p>
                <p className="text-red-700 text-sm">{t('contact_error_desc')}</p>
              </div>
            )}
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}